import type { ChronosphereDashboardSchema } from '../dashboard';
import type { ExternalPanelDefinition } from '../plugins/panels/external';
import { validateUrlProtocol } from './validate-url-protocol';

// Cap on the URL value sent over the wire and rendered in the toast. Bounds
// the worst case where an author paste-bombs a multi-KB `javascript:` payload —
// the violating scheme is at the head of the URL, so a head-truncated prefix
// is sufficient to identify what was rejected without flooding the UI.
const MAX_VIOLATION_VALUE_LENGTH = 100;

export interface DashboardURLViolation {
  /** Panel slug (key in `spec.panels`). */
  panel: string;
  /**
   * Author-facing panel display name (`panel.spec.display.name`), or empty
   * string when the author has not set one. The Go formatter uses this to
   * help authors saving many panels at once identify the offender.
   */
  panelDisplayName: string;
  /** Path within the panel, e.g. `plugin.spec.url` or `links[2].spec.url`. */
  field: string;
  /** Raw user-supplied value, truncated to {@link MAX_VIOLATION_VALUE_LENGTH}. */
  value: string;
}

function truncateValue(value: string): string {
  return value.length > MAX_VIOLATION_VALUE_LENGTH ? `${value.slice(0, MAX_VIOLATION_VALUE_LENGTH)}…` : value;
}

type ChronospherePanel = ChronosphereDashboardSchema['spec']['panels'][string];

// External plugin renders at runtime but is intentionally absent from
// chronospherePanelDefinitionSchema's plugin union. Widen so the External
// branch below is reachable per TS narrowing.
type WidenedPanel = Omit<ChronospherePanel, 'spec'> & {
  spec: Omit<ChronospherePanel['spec'], 'plugin'> & {
    plugin: ChronospherePanel['spec']['plugin'] | ExternalPanelDefinition;
  };
};

type DashboardLike = {
  spec?: {
    panels?: Record<string, WidenedPanel>;
  };
};

/**
 * Returns URL fields on a dashboard spec whose protocol is not in
 * {@link validateUrlProtocol}'s allowlist (`http`, `https`, `mailto`, plus
 * relative paths). Checks:
 * - `panels[*].spec.plugin.spec.url` when `plugin.kind === "External"` (iframe src)
 * - `panels[*].spec.links[*].spec.url` (anchor href)
 *
 * Allowlist instead of denylist so we don't have to enumerate every dangerous
 * scheme (`javascript:`, `data:`, `vbscript:`, `file:`, `blob:`, custom-app
 * schemes like `slack://`, etc.).
 *
 * Violations are returned in lexicographic panel-name order so error messages
 * are stable across runs.
 */
export function validateDashboardURLs(dashboardSpec: unknown): DashboardURLViolation[] {
  const violations: DashboardURLViolation[] = [];
  const panels = (dashboardSpec as DashboardLike)?.spec?.panels ?? {};

  for (const name of Object.keys(panels).sort()) {
    const panel = panels[name];
    const panelDisplayName = panel?.spec?.display?.name ?? '';
    const plugin = panel?.spec?.plugin;
    if (plugin?.kind === 'External') {
      const url = plugin.spec?.url;
      if (typeof url === 'string' && validateUrlProtocol(url) === null) {
        violations.push({ panel: name, panelDisplayName, field: 'plugin.spec.url', value: truncateValue(url) });
      }
    }
    // Array.isArray guards against adversarial JSON where `links` is a non-array
    // truthy value (string, number, object) — `?? []` only catches null/undefined.
    const links = panel?.spec?.links;
    if (Array.isArray(links)) {
      links.forEach((link, i) => {
        const url = link?.spec?.url;
        if (typeof url === 'string' && validateUrlProtocol(url) === null) {
          violations.push({
            panel: name,
            panelDisplayName,
            field: `links[${i}].spec.url`,
            value: truncateValue(url),
          });
        }
      });
    }
  }
  return violations;
}
